import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, BadgeCheck, BarChart3, Bell, BookOpen, Building2, CalendarRange, ClipboardList, Cog, Database, Download, FileSpreadsheet, Flag,
  GitBranch, History, LayoutDashboard, ListOrdered, Loader2, LogIn, Menu, ScanLine, Share2, ShoppingCart, Sparkles, Upload, Wallet,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { setTitle } from "@/lib/pwa";

const ICONS = {
  "layout-dashboard": LayoutDashboard, "shopping-cart": ShoppingCart, wallet: Wallet, "bar-chart-3": BarChart3, cog: Cog, "file-spreadsheet": FileSpreadsheet,
  "calendar-range": CalendarRange, upload: Upload, history: History, "git-branch": GitBranch, database: Database, "share-2": Share2, "building-2": Building2,
  "clipboard-list": ClipboardList, "badge-check": BadgeCheck, "scan-line": ScanLine, bell: Bell, "log-in": LogIn, sparkles: Sparkles,
};

const blobUrl = async (path, params) => URL.createObjectURL((await api.get(path, { params, responseType: "blob" })).data);

const Nav = ({ p, logo, entries, active, go }) => (
  <div className="flex h-full flex-col text-slate-100" data-testid="proposal-sidebar">
    <div className="border-b border-slate-800 px-5 py-5">
      {logo ? <img src={logo} alt={`${p.company} logo`} className="mb-3 max-h-10 max-w-[160px] object-contain" data-testid="proposal-sidebar-logo" />
        : <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-white/10 font-bold">{(p.company || "").slice(0, 2).toUpperCase()}</div>}
      <div className="truncate text-sm font-semibold" data-testid="proposal-sidebar-company">{p.company}</div>
      <div className="mt-0.5 line-clamp-2 text-xs text-slate-400" data-testid="proposal-sidebar-title">{p.title}</div>
    </div>
    <nav className="sidebar-scroll flex-1 space-y-0.5 overflow-y-auto px-3 py-4" data-testid="proposal-nav">
      {entries.map((e) => {
        const Icon = e.icon;
        return (
          <button key={e.id} type="button" onClick={() => go(e)} data-testid={`proposal-nav-${e.id}`}
            className={cn("flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-[13px] transition-colors", active === e.id ? "bg-teal-600/15 font-medium text-teal-300" : "text-slate-300 hover:bg-slate-800 hover:text-white")}>
            <Icon className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 truncate">{e.label}</span>{e.num && <span className="text-[10px] text-slate-500">{e.num}</span>}
          </button>
        );
      })}
    </nav>
    <div className="pb-safe border-t border-slate-800 p-3">
      <Link to="/documents" className="flex items-center gap-2 rounded-md px-2.5 py-2 text-[13px] text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="proposal-back-to-documents"><ArrowLeft className="h-4 w-4" /> Back to Documents</Link>
    </div>
  </div>
);

const Shot = ({ id, v, idx, s }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => { if (s.has_image) blobUrl(`/documents/${id}/image/${idx}`, { v }).then(setSrc).catch(() => setSrc(false)); }, [id, v, idx, s.has_image]);
  if (!s.has_image || src === false) return <div className="flex h-48 items-center justify-center rounded-xl bg-slate-100 text-sm font-medium text-slate-500">Screenshot unavailable</div>;
  if (!src) return <div className="flex h-48 items-center justify-center rounded-xl bg-slate-50"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  return <img src={src} alt={s.name} className={cn("rounded-lg border border-slate-200 shadow-[2px_3px_0_#E2E8F0]", s.mobile ? "mx-auto max-h-[720px] w-auto max-w-full" : "w-full")} data-testid={`proposal-shot-${idx}`} />;
};

const Section = ({ id, children, className }) => <section id={id} data-section={id} className={cn("scroll-mt-20 rounded-xl bg-white p-6 shadow-sm sm:p-10", className)}>{children}</section>;

export default function ProposalViewPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const v = sp.get("v") || undefined;
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [logo, setLogo] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [active, setActive] = useState("cover");
  const [open, setOpen] = useState(false);
  const main = useRef();

  useEffect(() => {
    if (!user) return;
    api.get(`/documents/${id}/view`, { params: { v } }).then((r) => {
      setP(r.data);
      setTitle(r.data.company, r.data.title);
      if (r.data.has_logo) blobUrl(`/documents/${id}/logo`).then(setLogo).catch(() => {});
      if (!r.data.renderable) blobUrl(`/documents/${id}/file`, { v: r.data.version }).then(setPdf);
    }).catch((e) => setP({ error: apiError(e) }));
  }, [id, v, user]);

  const entries = useMemo(() => (p?.sections ? [
    { id: "cover", label: "Cover", icon: BookOpen, page: p.pages.cover },
    { id: "contents", label: "Contents", icon: ListOrdered, page: p.pages.contents },
    ...p.sections.map((s, i) => ({ id: `sec-${i}`, label: s.name, icon: ICONS[s.icon] || Sparkles, page: s.page, num: String(i + 1).padStart(2, "0") })),
    { id: "closing", label: "Closing", icon: Flag, page: p.pages.closing },
  ] : []), [p]);

  useEffect(() => {
    if (!p?.renderable) return undefined;
    const obs = new IntersectionObserver((xs) => { const hit = xs.filter((x) => x.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]; if (hit) setActive(hit.target.dataset.section); },
      { rootMargin: "-20% 0px -65% 0px" });
    document.querySelectorAll("[data-section]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [p]);

  if (user === null) return <FullScreenLoader />;
  if (!user) return <Navigate to={localStorage.getItem("pnl_login_slug") ? `/login/${localStorage.getItem("pnl_login_slug")}` : "/login"} replace />;
  if (!p) return <FullScreenLoader />;
  if (p.error) return <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 p-6 text-center" data-testid="proposal-view-error"><p className="text-slate-700">{p.error}</p><Link to="/documents" className="text-sm font-medium text-teal-700 underline">Back to Documents</Link></div>;

  const go = (e) => {
    setOpen(false);
    setActive(e.id);
    if (p.renderable) document.getElementById(e.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    else setPage(e.page);
  };
  const download = async () => {
    try { Object.assign(document.createElement("a"), { href: await blobUrl(`/documents/${id}/file`, { v: p.version, download: true }), download: `${p.title}.pdf` }).click(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const nav = <Nav p={p} logo={logo} entries={entries} active={active} go={go} />;

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="proposal-view">
      <aside className="sticky top-0 hidden h-screen w-72 shrink-0 bg-[#0B1B33] lg:block">{nav}</aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="pt-safe w-[min(18rem,85vw)] border-0 bg-[#0B1B33] p-0"><SheetTitle className="sr-only">Proposal sections</SheetTitle>{nav}</SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="pt-safe sticky top-0 z-30 flex min-h-14 items-center gap-2 border-b border-slate-200 bg-white/90 px-3 backdrop-blur-md sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="proposal-menu-button"><Menu className="h-5 w-5" /></Button>
          <div className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900" data-testid="proposal-view-title">{p.title}<span className="ml-2 text-xs font-normal text-slate-400">v{p.version}</span></div>
          <Button onClick={download} className="gap-1.5 bg-teal-600 hover:bg-teal-700" data-testid="proposal-download-pdf"><Download className="h-4 w-4" /> <span className="hidden sm:inline">Download PDF</span></Button>
        </header>
        {!p.renderable ? (
          pdf ? <iframe key={page} title={p.title} src={`${pdf}#page=${page}`} className="h-[calc(100dvh-3.5rem)] w-full border-0" data-testid="proposal-pdf-fallback" />
            : <div className="p-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <main ref={main} className="mx-auto w-full max-w-4xl space-y-6 px-3 py-6 sm:px-6 sm:py-10" data-testid="proposal-sections">
            <Section id="cover" className="relative overflow-hidden bg-[#0B1B33] text-white sm:py-20">
              <div className="absolute left-0 top-1/3 h-1/3 w-2 bg-teal-500" />
              {logo && <img src={logo} alt="" className="mb-8 max-h-20 max-w-[260px] object-contain" />}
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-300">{p.company}</div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight sm:text-5xl" data-testid="proposal-cover-title">{p.title}</h1>
              {p.client && <p className="mt-6 text-slate-300">Prepared for {p.client}</p>}
              <p className="mt-1 text-slate-300">{p.date}</p>
            </Section>
            <Section id="contents">
              <h2 className="text-2xl font-bold text-[#0B1B33]">Contents</h2>
              <ol className="mt-5 divide-y divide-slate-100">
                {entries.filter((e) => e.num).map((e) => (
                  <li key={e.id}><button type="button" onClick={() => go(e)} className="flex w-full items-center gap-3 py-2.5 text-left text-slate-800 hover:text-teal-700">
                    <span className="w-6 text-xs font-semibold text-teal-700">{e.num}</span><span className="flex-1">{e.label}</span><span className="text-xs text-slate-400">p. {e.page}</span>
                  </button></li>
                ))}
              </ol>
              {p.intro && <div className="mt-8 border-t border-slate-100 pt-6"><div className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">Introduction</div><p className="mt-2 whitespace-pre-line leading-relaxed text-slate-700">{p.intro}</p></div>}
            </Section>
            {p.sections.map((s, i) => (
              <Section key={s.key + i} id={`sec-${i}`}>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">Module {String(i + 1).padStart(2, "0")}</div>
                <h2 className="mt-1 text-2xl font-bold text-[#0B1B33]" data-testid={`proposal-section-name-${i}`}>{s.name}</h2>
                <p className="mt-3 whitespace-pre-line leading-relaxed text-slate-700">{s.description}</p>
                <figure className="mt-6"><Shot id={id} v={p.version} idx={i} s={s} />
                  <figcaption className="mt-2 text-center text-xs text-slate-500">Figure {i + 1}. {s.name}{s.mobile ? " (mobile view)" : ""} · amounts and names blurred</figcaption></figure>
              </Section>
            ))}
            <Section id="closing" className="bg-[#0B1B33] py-16 text-center text-white">
              {logo && <img src={logo} alt="" className="mx-auto mb-6 max-h-16 max-w-[220px] object-contain" />}
              <h2 className="text-3xl font-bold">Thank you</h2>
              <p className="mt-2 text-slate-300">{p.company}</p>
            </Section>
          </main>
        )}
      </div>
    </div>
  );
}
